var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Editor from '@draft-js-plugins/editor';
import { ContentState, convertFromRaw, convertToRaw, EditorState, RichUtils, } from 'draft-js';
import { WidgetType } from '@common/api';
import { useLanguage } from '@base/LanguageProvider';
import WidgetMenu from '@main/dashboard/WidgetMenu';
import { useDebounce } from '@hooks/useDebounce';
import { Copy, Delete, Edit } from '@shared/icons/icons';
import TextEditMenu from '@shared/textEdit/TextEditMenu';
import { customTextStyles } from '@shared/textEdit/editorStyles';
import { useAuth } from '../../../providers/AuthProvider';
import { parseWidgetConfig } from '@data/widgets/widgetConfig';
import { useCloneWidget, useDeleteWidget, useUpdateWidget } from '@data/widgets/widgetActions';
import { WidgetBox, WidgetContent } from '@styled/Widget';
function getEditorStateFromRawContent(rawContent) {
    try {
        var content = rawContent === undefined ? ContentState.createFromText('') : convertFromRaw(rawContent);
        var editorState = EditorState.createWithContent(content);
        return editorState;
    }
    catch (error) {
        // console.error(error);
        return EditorState.createWithContent(ContentState.createFromText('Content of text tile is corrupt...'));
    }
}
function TextTile(_a) {
    var widget = _a.widget, documentActions = _a.documentActions;
    var user = useAuth().user;
    var L = useLanguage().L;
    var wrapperRef = useRef(null);
    var editorRef = useRef(null);
    var updateWidget = useUpdateWidget();
    var cloneWidget = useCloneWidget();
    var deleteWidget = useDeleteWidget();
    var widgetConfig = useMemo(function () { return parseWidgetConfig(widget.config, WidgetType.TEXTTILE); }, [widget.config]);
    var _b = __read(useState(function () { return getEditorStateFromRawContent(widgetConfig.editorRawContent); }), 2), editorState = _b[0], setEditorState = _b[1];
    var debouncedEditorState = useDebounce(editorState, 2000);
    var _c = __read(useState(false), 2), editing = _c[0], setEditing = _c[1];
    var setWidgetEditing = function (newEditing) {
        setEditing(newEditing);
    };
    var requestWidgetUpdate = function () {
        var widgetCopy = __assign({}, widget);
        var widgetCopyConfig = __assign({}, widgetConfig);
        var currentContent = editorState.getCurrentContent();
        var rawContent = convertToRaw(currentContent);
        widgetCopyConfig.editorRawContent = rawContent;
        var newConfig = JSON.stringify(widgetCopyConfig);
        if (newConfig !== widgetCopy.config) {
            widgetCopy.config = newConfig;
            updateWidget.mutate(widgetCopy);
        }
    };
    useEffect(function () {
        requestWidgetUpdate();
    }, [debouncedEditorState]);
    var handleKeyCommand = function (command, editorState) {
        var newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState !== null) {
            setEditorState(newState);
            return 'handled';
        }
        return 'not-handled';
    };
    var widgetMenuItems = [
        {
            icon: Edit,
            actionName: L('Edit'),
            isDisabled: !(user === null || user === void 0 ? void 0 : user.classActions.documentClassActions.canUpdate),
            action: function () { return setWidgetEditing(true); },
        },
        {
            icon: Copy,
            actionName: L('Duplicate'),
            isDisabled: !(user === null || user === void 0 ? void 0 : user.classActions.documentClassActions.canDuplicate),
            action: function () { return cloneWidget.mutate(widget); },
        },
        { spacer: true },
        {
            icon: Delete,
            actionName: L('Delete Tile'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canDelete),
            action: function () { return deleteWidget.mutate(widget.id); },
            destructive: true,
        },
    ];
    return (React.createElement(WidgetBox, { ref: wrapperRef, style: editing ? { border: '1px dashed rgba(0,0,255,0.5)' } : {} },
        React.createElement(WidgetMenu, { menuItems: widgetMenuItems, wrapperRef: wrapperRef, visible: !editing }),
        React.createElement(TextEditMenu, { wrapperRef: wrapperRef, editorState: editorState, updateEditorState: setEditorState, visible: editing }),
        React.createElement(WidgetContent, { onDoubleClick: function () {
                var _a;
                setWidgetEditing(true);
                (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, onMouseDown: function (e) {
                editing ? e.stopPropagation() : null;
            } },
            React.createElement(Editor, { ref: editorRef, readOnly: !editing, customStyleMap: customTextStyles, editorState: editorState, onChange: setEditorState, handleKeyCommand: handleKeyCommand, onBlur: function () { return setWidgetEditing(false); } }))));
}
export default TextTile;
